import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SectionCard, fmtDate } from '../../shared/Panel';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { recordStateKey } from '../../shared/recordStateKey';
import { toErrorMessage } from './orderActionsApi';
import { CarrierSuggestions } from './CarrierSuggestions';
import {
  clipboardText,
  confirmInquirySent,
  createInquiry,
  fetchCarrierRecipients,
  fetchInquiries,
  inquiryRecipient,
  INQUIRY_CHECK_FAILED,
  INQUIRY_CHECKING,
  inquiryBlockReason,
  isSimulatedBody,
  hasSignaturePlaceholder,
  SIGNATURE_NAME_PLACEHOLDER,
  LANGUAGE_LABELS,
  mailtoLink,
  MASKING_CHOICES,
  maskingShortLabel,
  searchCarriers,
  SEND_HINT,
  templateLabel,
  updateInquiry,
  type CarrierInquiry,
  type InquiryContext,
  type InquiryLanguage,
  type TmsRecipientContact,
} from './carrierInquiriesApi';
import { contactCodeLabel } from '../../shared/contactCodes';

export interface PickedCarrier {
  id: number;
  name: string;
}

type Contact = NonNullable<CarrierInquiry['contact']>;

const NOWRAP = { whiteSpace: 'nowrap' } as const;
/** Pola wyboru nie szersze niż pola akcji na karcie zlecenia (#271). */
const FIELD_MAX = { maxWidth: 480 } as const;

const contactLabel = (c: Contact) =>
  [c.name, c.role, c.channel].filter((x) => x && x.trim() !== '').join(' · ') || `kontakt #${c.id}`;

/** Kontakt z TMS w liście adresatów: osoba albo e-mail, rodzaj po polsku, „z TMS”. */
const tmsContactLabel = (c: TmsRecipientContact) =>
  `${[c.corrName ?? c.email, contactCodeLabel(c.typeOfActivity)].filter((x) => x && x.trim() !== '').join(' · ') || `kontakt #${c.id}`} (z TMS)`;

/**
 * Wybór adresata w jednym polu: `c:<id>` — kontakt wpisany przez Trade,
 * `t:<id>` — kontakt z TMS (16.10.2026, pyt. 56), pusty — bez osoby.
 */
const recipientInput = (key: string): { contactId: number | null; tmsContactId?: number } => {
  const [kind, id] = key.split(':');
  if (kind === 'c') return { contactId: Number(id) };
  if (kind === 't') return { contactId: null, tmsContactId: Number(id) };
  return { contactId: null };
};

/**
 * Potwierdzenie przed „wysłane ręcznie” — zdanie dla reguły klienta zamiast
 * kodu wstawionego w środek („Reguła podwykonawcy warunkowy — …”, 01.10.2026).
 */
const confirmationLabel = (rule: string) =>
  rule === 'warunkowy'
    ? 'Potwierdzam, że warunki klienta są spełnione i to zlecenie można przekazać przewoźnikowi'
    : 'Potwierdzam, że to zlecenie można przekazać przewoźnikowi (dla tego klienta nie ustalono reguły podwykonawstwa)';

/**
 * Formularz nowego szkicu: przewoźnik (z listy I.08 albo z kartoteki),
 * adresat z kontaktów przewoźnika, język i — gdy spółka nie ma trybu —
 * maskowanie lokalizacji.
 */
function PrepareForm({
  userOfferId,
  context,
  picked,
  onPick,
  onCreated,
}: {
  userOfferId: number | string;
  context: InquiryContext;
  picked: PickedCarrier | null;
  onPick: (c: PickedCarrier | null) => void;
  onCreated: () => void;
}) {
  const notify = useNotify();
  const [options, setOptions] = useState<PickedCarrier[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tmsContacts, setTmsContacts] = useState<TmsRecipientContact[]>([]);
  const [recipient, setRecipient] = useState('');
  const [language, setLanguage] = useState<InquiryLanguage>('pl');
  const [maskingMode, setMaskingMode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRecipient('');
    setContacts([]);
    setTmsContacts([]);
    if (!picked) return;
    fetchCarrierRecipients(picked.id)
      .then((r) => {
        setContacts(r.contacts);
        setTmsContacts(r.tmsContacts);
      })
      .catch(() => {
        setContacts([]);
        setTmsContacts([]);
      });
  }, [picked]);

  const search = async (q: string) => {
    if (q.trim().length < 2) return;
    try {
      setOptions(await searchCarriers(q.trim()));
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await createInquiry(userOfferId, {
        carrierId: picked.id,
        ...recipientInput(recipient),
        language,
        ...(context.masking.requiresChoice ? { maskingMode } : {}),
      });
      notify('Przygotowano szkic zapytania — sprawdź treść przed wysłaniem.', { type: 'info' });
      onPick(null);
      onCreated();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Autocomplete
        size="small"
        sx={FIELD_MAX}
        options={options}
        value={picked}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={(x) => x}
        onInputChange={(_, q, reason) => {
          if (reason === 'input') void search(q);
        }}
        onChange={(_, v) => onPick(v)}
        renderInput={(params) => (
          <TextField {...params} label="Przewoźnik z kartoteki" placeholder="nazwa albo miasto" />
        )}
      />
      {picked && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="Adresat"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">bez wskazanej osoby</MenuItem>
              {contacts.map((c) => (
                <MenuItem key={`c:${c.id}`} value={`c:${c.id}`}>
                  {contactLabel(c)}
                </MenuItem>
              ))}
              {/* Kontakty z TMS (16.10.2026, pyt. 56) — tylko do wyboru, bez edycji. */}
              {tmsContacts.map((c) => (
                <MenuItem key={`t:${c.id}`} value={`t:${c.id}`}>
                  {tmsContactLabel(c)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Język"
              value={language}
              onChange={(e) => setLanguage(e.target.value as InquiryLanguage)}
              sx={{ minWidth: 140 }}
            >
              {context.languages.map((l) => (
                <MenuItem key={l} value={l}>
                  {LANGUAGE_LABELS[l] ?? l}
                </MenuItem>
              ))}
            </TextField>
            {context.masking.requiresChoice && (
              <TextField
                select
                size="small"
                label="Maskowanie lokalizacji"
                value={maskingMode}
                onChange={(e) => setMaskingMode(e.target.value)}
                sx={{ minWidth: 260 }}
                helperText="Spółka nie ma ustawionego trybu maskowania — wybierz go dla tego zapytania"
              >
                {MASKING_CHOICES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button
              size="small"
              variant="contained"
              sx={NOWRAP}
              disabled={busy || (context.masking.requiresChoice && maskingMode === '')}
              onClick={() => void submit()}
            >
              Przygotuj zapytanie do {picked.name}
            </Button>
            <Button size="small" onClick={() => onPick(null)}>
              Anuluj
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}

/**
 * Jeden szkic: edycja treści, kopiowanie, „Wyślij” (program pocztowy
 * tradera) i potwierdzenie ręcznej wysyłki.
 */
function InquiryCard({
  userOfferId,
  inquiry,
  context,
  onChanged,
}: {
  userOfferId: number | string;
  inquiry: CarrierInquiry;
  context: InquiryContext;
  onChanged: () => void;
}) {
  const notify = useNotify();
  const [subject, setSubject] = useState(inquiry.subject);
  const [body, setBody] = useState(inquiry.body);
  const [confirmed, setConfirmed] = useState(false);
  const [exception, setException] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSubject(inquiry.subject);
    setBody(inquiry.body);
  }, [inquiry.subject, inquiry.body]);

  const draft = inquiry.status === 'draft';
  const simulated = isSimulatedBody(body);
  const dirty = subject !== inquiry.subject || body !== inquiry.body;
  const gate = context.subcontractor;
  // Adresat: kontakt z kartoteki albo kopia kontaktu z TMS (16.10.2026).
  const addressee = inquiryRecipient(inquiry, contactCodeLabel);
  // To, co widać w polach (także przed zapisem) — jak przy „Kopiuj”.
  const mail = mailtoLink({ subject, body, contact: addressee });

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      notify(message, { type: 'info' });
      onChanged();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(clipboardText({ subject, body }));
      notify('Skopiowano temat i treść', { type: 'info' });
    } catch {
      notify('Nie udało się skopiować — zaznacz treść i skopiuj ręcznie', { type: 'warning' });
    }
  };

  /**
   * Treść za długa na odnośnik `mailto:` — program pocztowy dostaje temat
   * i notatkę „wklej treść”, a treść idzie do schowka. Kliknięcie nie czeka
   * na schowek: program pocztowy otwiera się jak zawsze.
   */
  const copyForMail = async () => {
    try {
      await navigator.clipboard.writeText(body);
      notify('Treść jest za długa dla programu pocztowego — skopiowano ją do schowka, wklej ją w wiadomość', {
        type: 'info',
      });
    } catch {
      notify('Treść jest za długa dla programu pocztowego — skopiuj ją przyciskiem „Kopiuj” i wklej w wiadomość', {
        type: 'warning',
      });
    }
  };

  const gateBlocked =
    (gate.requiresConfirmation && !confirmed) ||
    (gate.requiresException && (!gate.mayGrantException || !exception || reason.trim() === ''));

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2">{inquiry.carrier.name}</Typography>
        {draft ? (
          <Chip size="small" label="szkic" />
        ) : (
          <Chip
            size="small"
            color="success"
            label={`wysłane ręcznie ${fmtDate(inquiry.sentAt)}${inquiry.sentBy ? ` · ${inquiry.sentBy.username}` : ''}`}
          />
        )}
        {/* Praca dwutorowa (16.10.2026): flota obsadziła zlecenie — zapytanie
            nieaktualne. Pole niesie API tylko z przełącznikiem `tradeDualTrack`. */}
        {inquiry.outdated ? (
          <Tooltip title={inquiry.outdatedReason ?? ''}>
            <Chip size="small" color="warning" variant="outlined" label="nieaktualne" data-testid="inquiry-outdated" />
          </Tooltip>
        ) : null}
        {simulated && <Chip size="small" color="warning" label="treść z oznaczeniem symulacji" />}
        {inquiry.carrier.active === false && (
          <Chip size="small" color="error" label="współpraca wstrzymana (według kartoteki)" />
        )}
        {/* Nazwy zamiast kodów trybu i wzoru; forma bezosobowa (01.10.2026). */}
        <Typography variant="caption" color="text.secondary">
          Przygotowane przez {inquiry.createdBy?.username ?? '—'} {fmtDate(inquiry.createdAt)} · lokalizacja:{' '}
          {maskingShortLabel(inquiry.maskingMode)} · {templateLabel(inquiry.template)}
        </Typography>
      </Stack>

      {addressee && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {addressee.fromTms ? 'Adresat (z TMS)' : 'Adresat'}: {addressee.label}
          {addressee.email ? ` · ${addressee.email}` : ''}
          {addressee.phone ? ` · tel. ${addressee.phone}` : ''}
        </Typography>
      )}

      {draft && hasSignaturePlaceholder(body) && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Przed wysłaniem wpisz w podpisie swoje imię i nazwisko w miejsce „{SIGNATURE_NAME_PLACEHOLDER}”.
          Żeby podpis wypełniał się sam, poproś administratora o wpisanie imienia i nazwiska w Twoim
          koncie.
        </Alert>
      )}

      {inquiry.omitted.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          W treści pominięto dane, których brakuje w zleceniu: {inquiry.omitted.join(', ')}. Jeśli je
          znasz, uzupełnij treść ręcznie — system nie wpisuje danych na domysł.
        </Alert>
      )}

      <Stack spacing={1}>
        <TextField
          size="small"
          label="Temat"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          slotProps={{ htmlInput: { readOnly: !draft } }}
        />
        <TextField
          size="small"
          label="Treść"
          multiline
          minRows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          slotProps={{ htmlInput: { readOnly: !draft } }}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1, '& .MuiButton-root': NOWRAP }}
      >
        <Button size="small" variant="outlined" onClick={() => void copy()}>
          Kopiuj
        </Button>
        {/* Zawsze przy szkicu (omówienie 01.10.2026) — bez adresu w kartotece
            adresat zostaje pusty i trader wpisuje go w programie pocztowym. */}
        {draft && (
          <Tooltip title={SEND_HINT} describeChild>
            <Button
              size="small"
              variant="outlined"
              href={mail.href}
              onClick={mail.bodyIncluded ? undefined : () => void copyForMail()}
            >
              Wyślij
            </Button>
          </Tooltip>
        )}
        {draft && (
          <Button
            size="small"
            variant="outlined"
            disabled={busy || !dirty}
            onClick={() =>
              void run(
                () => updateInquiry(userOfferId, inquiry.id, { subject, body }),
                'Zapisano zmiany',
              )
            }
          >
            Zapisz zmiany
          </Button>
        )}
      </Stack>
      {draft && !mail.bodyIncluded && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          Treść jest za długa dla programu pocztowego — „Wyślij” otworzy wiadomość z tematem
          i skopiuje treść do schowka.
        </Typography>
      )}

      {draft && (
        <Box sx={{ mt: 1 }}>
          {/* Typ klienta z TMS (16.10.2026, pyt. 38) — podpowiedź, nie reguła. */}
          {gate.customerTypeHint && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
              {gate.customerTypeHint}
            </Typography>
          )}
          {gate.requiresConfirmation && (
            <FormControlLabel
              control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />}
              label={confirmationLabel(gate.regula)}
            />
          )}
          {gate.requiresException && !gate.mayGrantException && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {gate.contractor?.legalName ? `Klient ${gate.contractor.legalName}` : 'Klient'} zakazuje
              przekazywania zleceń podwykonawcom. Wyjątku może udzielić tylko osoba z dostępem do sekcji
              Administracja.
            </Alert>
          )}
          {gate.requiresException && gate.mayGrantException && (
            <Stack spacing={1} sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={exception} onChange={(e) => setException(e.target.checked)} />}
                label="Udzielam wyjątku od zakazu podwykonawstwa"
              />
              <TextField
                size="small"
                label="Powód wyjątku"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Stack>
          )}
          {simulated && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Treść zaczyna się od „Symulacja treści:” — Marathon nie przysłał jeszcze wzoru. Przewoźnik
              zobaczy to oznaczenie, jeśli go nie usuniesz.
            </Alert>
          )}
          <Button
            size="small"
            variant="contained"
            color="success"
            sx={NOWRAP}
            disabled={busy || dirty || gateBlocked}
            onClick={() =>
              void run(
                () =>
                  confirmInquirySent(userOfferId, inquiry.id, {
                    eligibilityConfirmed: confirmed || undefined,
                    banException: exception || undefined,
                    reason: reason.trim() || undefined,
                  }),
                'Oznaczono jako wysłane ręcznie',
              )
            }
          >
            Oznacz jako wysłane ręcznie
          </Button>
          {dirty && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Najpierw zapisz zmiany — potwierdzasz wysłanie dokładnie tej treści.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Zapytanie do przewoźnika na zleceniu Trade (I.10).
 *
 * Status 29.09.2026: zapytanie generuje się NA ZLECENIU z jednego
 * standardowego wzoru — nie wzór per przewoźnik. Przewoźnik jest adresatem:
 * trader wybiera go z listy „Przewoźnicy z historią na tej relacji” (przycisk
 * „Przygotuj zapytanie” w wierszu) albo z kartoteki, a treść jest ta sama.
 * Dopóki Marathon nie przyśle wzoru, treść zaczyna się od „Symulacja treści:”.
 *
 * System PRZYGOTOWUJE szkic (lokalizacja zamaskowana jak na giełdzie, bez
 * ceny, braki pominięte), trader go sprawdza, poprawia i sam wysyła —
 * „Wyślij” otwiera jego program pocztowy z gotową wiadomością, „Kopiuj”
 * daje treść do komunikatora. „Oznacz jako wysłane ręcznie” to jego
 * oświadczenie — system nic nie wysyła. Historia zapytań zlecenia jest pod
 * formularzem, także gdy zlecenie wyszło już z Trade.
 *
 * Dane ładują się od nowa przy każdej zmianie stanu zlecenia, nie tylko
 * przy zmianie rekordu: po przekazaniu do Trade sekcja pokazywała dalej
 * „zlecenie nie jest przekazane do Trade” (omówienie 01.10.2026).
 */
export function CarrierInquiries({
  picked,
  onPick,
  onAvailability,
}: {
  picked: PickedCarrier | null;
  onPick: (c: PickedCarrier | null) => void;
  /**
   * Czy przy zleceniu da się dziś przygotować zapytanie: `null` = tak, tekst
   * = powód (z kontekstu API). Dla przycisków „Przygotuj zapytanie” w tabeli
   * przewoźników z historią — mają być wyszarzone, zanim ktoś kliknie.
   */
  onAvailability?: (blockedReason: string | null) => void;
}) {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchInquiries>> | null>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const id = record?.id;
  const stateKey = recordStateKey(record);
  // Najświeższa obsługa bez przeładowania przy każdym renderze rodzica.
  const report = useRef(onAvailability);
  report.current = onAvailability;
  // Numer ostatniego odczytu — starsza odpowiedź nie nadpisze nowszej.
  const lastLoad = useRef(0);

  const load = useCallback(async () => {
    if (id === undefined) return;
    const call = ++lastLoad.current;
    try {
      const next = await fetchInquiries(id);
      if (!stillMounted() || call !== lastLoad.current) return;
      setData(next);
      report.current?.(inquiryBlockReason(next.context));
    } catch (e) {
      if (!stillMounted() || call !== lastLoad.current) return;
      notify(toErrorMessage(e), { type: 'error' });
      report.current?.(INQUIRY_CHECK_FAILED);
    }
  }, [id, notify, stillMounted]);

  // Stan zlecenia w zależnościach celowo: `load` się nie zmienia, a dane
  // (odmowa, bramki) zależą od stanu, który właśnie się zmienił.
  useEffect(() => {
    void load();
  }, [load, stateKey]);

  // Przewoźnik wybrany w tabeli wyżej — formularz bywa poza ekranem.
  useEffect(() => {
    if (picked) anchor.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [picked]);

  if (!record || !data) return null;
  const { context, inquiries } = data;

  return (
    <div ref={anchor}>
      <SectionCard title="Zapytanie do przewoźnika">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Jeden wzór zapytania dla zlecenia — zmienia się tylko adresat. Przewoźnika wybierz
          przyciskiem „Przygotuj zapytanie” w tabeli przewoźników z historią albo wyszukaj go
          w kartotece. System przygotowuje treść, ale niczego nie wysyła: sprawdź ją, otwórz
          w programie pocztowym przyciskiem „Wyślij” albo skopiuj do komunikatora i wyślij
          samodzielnie. Lokalizacje są zamaskowane jak na giełdzie, a cena nie jest podawana.
        </Typography>

        {context.template?.simulated && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Wzór zapytania jest tymczasowy — czekamy na standardowy wzór od Marathonu. Do tego czasu
            treść zaczyna się od „Symulacja treści:”.
          </Alert>
        )}

        {context.refusal && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {context.refusal}
          </Alert>
        )}

        {context.allowed && (
          <PrepareForm
            userOfferId={record.id}
            context={context}
            picked={picked}
            onPick={onPick}
            onCreated={() => void load()}
          />
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Zapytania na tym zleceniu ({inquiries.length})
        </Typography>
        {inquiries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Na tym zleceniu nie przygotowano jeszcze zapytania.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {inquiries.map((i) => (
              <InquiryCard
                key={i.id}
                userOfferId={record.id}
                inquiry={i}
                context={context}
                onChanged={() => void load()}
              />
            ))}
          </Stack>
        )}
      </SectionCard>
    </div>
  );
}

/**
 * Trade na karcie zlecenia: przewoźnicy z historią (I.08) i zapytanie do
 * przewoźnika (I.10). Wybór w tabeli przewoźników wypełnia formularz
 * zapytania niżej. Stan wyboru należy do zlecenia — przejście na inne
 * zlecenie zaczyna od zera (`recordScoped`).
 *
 * Czy zapytanie da się przygotować, mówi kontekst z odczytu zapytań — ten
 * sam, z którego sekcja zapytań pokazuje odmowę. Przyciski w tabeli są
 * wyszarzone z tym powodem, zanim ktoś kliknie (omówienie 01.10.2026);
 * do pierwszej odpowiedzi czekają.
 */
function TradeCarriersPanel({
  inquiriesEnabled = true,
}: {
  /**
   * Moduł zapytań do przewoźników spółki zlecenia (T.01, 06.10.2026).
   * Wyłączony — sekcji zapytań i przycisków „Przygotuj zapytanie” nie ma.
   */
  inquiriesEnabled?: boolean;
}) {
  const [picked, setPicked] = useState<PickedCarrier | null>(null);
  const [inquiryBlocked, setInquiryBlocked] = useState<string | null>(INQUIRY_CHECKING);
  if (!inquiriesEnabled) return <CarrierSuggestions />;
  return (
    <>
      <CarrierSuggestions onPrepareInquiry={setPicked} prepareInquiryBlocked={inquiryBlocked} />
      <CarrierInquiries picked={picked} onPick={setPicked} onAvailability={setInquiryBlocked} />
    </>
  );
}

export const TradeCarriers = recordScoped(TradeCarriersPanel);
